import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a date string to a localized date format
 * Handles dates without timezone to avoid timezone shift issues
 * @param dateString - The date string to format (YYYY-MM-DD or ISO format)
 * @param locale - The locale to use for formatting (default: 'en-US')
 * @returns Formatted date string
 */
export function formatDate(dateString: string, locale: string = 'en-US'): string {
  try {
    // Extract just the date part if it's an ISO string
    const datePart = dateString.split('T')[0]
    
    // Parse the date components manually to avoid timezone issues
    const [year, month, day] = datePart.split('-').map(Number)
    
    // Create date in local timezone (month is 0-indexed)
    const date = new Date(year, month - 1, day)
    
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  } catch (error) {
    console.error('Error formatting date:', error)
    return dateString
  }
}

/**
 * Extract date in YYYY-MM-DD format without timezone offset issues
 * @param dateString - ISO date string from API
 * @returns Date string in YYYY-MM-DD format
 */
export function extractDateWithoutTimezone(dateString: string | undefined | null): string {
  if (!dateString) return ''
  
  // If already in YYYY-MM-DD format, return as is
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString
  }
  
  // Extract date part before 'T' if present
  const datePart = dateString.split('T')[0]
  return datePart || ''
}

/**
 * Convert an ISO date string to a local Date object at start of day
 * Strips timezone to avoid off-by-one errors in date comparisons
 * @param value - ISO date string (e.g., "2024-01-15T00:00:00Z")
 * @returns Date object at start of local day or null if invalid
 */
const toLocalDate = (value: string): Date | null => {
  const datePart = extractDateWithoutTimezone(value)
  if (!datePart) {
    return null
  }
  const parsed = new Date(`${datePart}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * Get the current version of commission plans grouped by CommissionPlanId
 * Filters out "Replaced" versions and selects the version with the most recent
 * effective date that is <= today's date
 * @param plans - Array of commission plan configurations
 * @returns Array of current commission plans
 */
export function getMostRecentPlans<T extends { Data: { CommissionPlanId: string, EffectiveDate: string, Status: string } }>(plans: T[]): T[] {
  const today = new Date()
  today.setHours(0, 0, 0, 0) // Reset time to start of day for comparison
  const todayTimestamp = today.getTime()
  
  // Filter out "Replaced" versions
  const nonReplacedPlans = plans.filter(plan => 
    plan.Data.Status?.toLowerCase() !== 'replaced'
  )
  
  // Group by CommissionPlanId
  const groupedPlans = nonReplacedPlans.reduce((acc, plan) => {
    const id = plan.Data.CommissionPlanId
    if (!acc[id]) {
      acc[id] = []
    }
    acc[id].push(plan)
    return acc
  }, {} as Record<string, T[]>)

  // For each group, find the current version based on effective date
  return Object.values(groupedPlans).map(group => {
    const datedPlans = group
      .map(plan => ({ plan: plan, date: toLocalDate(plan.Data.EffectiveDate) }))
      .filter((entry): entry is { plan: T; date: Date } => entry.date !== null)
    
    if (datedPlans.length === 0) {
      return group[0]
    }
    
    // Filter versions where effective date <= today
    const effectiveVersions = datedPlans.filter(({ date }) => date.getTime() <= todayTimestamp)
    
    // If there are versions effective as of today, get the most recent one
    if (effectiveVersions.length > 0) {
      return effectiveVersions.reduce((mostRecent, current) =>
        current.date > mostRecent.date ? current : mostRecent
      ).plan
    }
    
    // If no versions are effective yet, return the one with the earliest future date
    return datedPlans.reduce((earliest, current) =>
      current.date < earliest.date ? current : earliest
    ).plan
  })
}

